import {
    Box,
    Grid2 as Grid,
    OutlinedInput,
    InputAdornment,
    Stack,
    IconButton,
    Collapse,
    Divider,
    Button,
    Typography,
} from "@mui/material";
import React, { useEffect, useRef } from "react";
import ActivityConversation from "../../../../Components/Activity/ActivityConversation/activity-conversation";

import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import AddIcon from "@mui/icons-material/Add";

import SearchIcon from "@mui/icons-material/Search";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import CollectionsBookmarkOutlinedIcon from "@mui/icons-material/CollectionsBookmarkOutlined";
import ActivityContext from "../../../../Components/Activity/ActivityContext/activity-context";
import { useToggle } from "../../../../Utils/Hooks/useToggle";
import CommentForm from "../../../../Components/Misc/CommentForm/comment-form";
import ActivityComment from "../../../../Components/Activity/AcitivityDetails/activity-comment";
import { useSelection } from "../../../../Utils/Hooks/useSelection";
import { ActivityKind } from "../../../../Core/Api/Types/activity-kind";
import { cacheService, messengerNotifier, messengerService } from "../../../../Core/Api/services";
import { DbMessengerGroupInfos } from "../../../../Core/Api/Types/db-messenger-group-infos";
import { ModalContext } from "../../../../Components/Modals";
import ModalCreateConversation, {
    ModalCreateConversationElementProps,
} from "../../../../Components/Modals/ModalCreateConversation/modal-create-conversation";
import { DbMessengerMessageInfos } from "../../../../Core/Api/Types/db-messenger-message-infos";
import { DbUserBasicInfos } from "../../../../Core/Api/Types/db-user-basic-infos";
import {
    AddGroupMessage,
    DeleteGroupMessage,
    GetGroupMessages,
    GetGroupMessageSelected,
    SetGroupMessages,
    UpdateGroupMessage,
    SetGroupMessageSelected,
    NewMessage,
    DeleteMessage,
    SetMessageBadgeNumber,
} from "../../../../Redux/Reducers/redux-slice-group-messages";
import { useDispatch, useSelector } from "react-redux";
import { HubConnectionState } from "@microsoft/signalr";
import ModalConfirmAction, {
    ModalConfirmActionElementProps,
} from "../../../../Components/Modals/ModalConfirmAction/modal-confirm-action";
import EmptyListSlot from "../../../../Components/Misc/EmptyListSlot";
import { useTranslation } from "react-i18next";

function DashboardConversations() {
    const { t } = useTranslation();
    const { active, toggle } = useToggle(false);
    const {
        isSelected: isConversationSelected,
        select: selectConversation,
        selected: selectedMessage,
    } = useSelection("0");
    const [searchFilter, setSearchFilter] = React.useState<string>("");
    const dispatch = useDispatch();
    const modalContext = React.useContext(ModalContext);
    const { openModal } = { ...modalContext };

    const myUserId = React.useMemo(() => cacheService.GetUserInfo()!.id, []);

    const groupMessages = useSelector(GetGroupMessages);
    const conversationSelected = useSelector(GetGroupMessageSelected);

    const bottomConvRef = useRef<null | HTMLDivElement>(null);

    const onClickConfirmModal = (
        isCreation: boolean,
        usersSelected: DbUserBasicInfos[],
        name: string,
        groupId?: number
    ) => {
        if (messengerNotifier.connection) {
            if (isCreation)
                messengerNotifier.connection
                    ?.invoke(
                        "CreateGroup",
                        usersSelected.map((user) => user.id),
                        name
                    )
                    .then(function (response: DbMessengerGroupInfos) {
                        if (!groupMessages.find((group) => group.id === response.id)) {
                            dispatch(AddGroupMessage(response));
                        }
                    });
            else
                messengerNotifier.connection
                    ?.invoke(
                        "UpdateGroup",
                        groupId,
                        usersSelected.map((user) => user.id),
                        name
                    )
                    .then(function (response: DbMessengerGroupInfos) {
                        dispatch(UpdateGroupMessage(response));
                    });
        }
    };

    const handleOpenModalNewConversation = React.useCallback(
        (users?: DbUserBasicInfos[], name?: string, id?: number) => {
            if (!openModal) return;
            const onClose: ModalCreateConversationElementProps["onClose"] = () => { };

            openModal<ModalCreateConversationElementProps>(ModalCreateConversation, {
                onClose,
                users: users,
                groupName: name,
                groupId: id,
                onClickConfirmModal,
            });
        },
        [openModal, onClickConfirmModal]
    );

    const loadGroupMessages = () => {
        messengerService.ListGroups().then(function (response) {
            if (response.success && response.groups) {
                dispatch(
                    SetGroupMessages(
                        response.groups.sort(function (a, b) {
                            if ((a.lastUpdate ? a.lastUpdate : a.creationDate)! < (b.lastUpdate ? b.lastUpdate : b.creationDate)!)
                                return 1;
                            else return -1;
                        })
                    )
                );
                if (response.groups.length > 0) {
                    onSelect(response.groups[0].id);
                    dispatch(SetGroupMessageSelected(response.groups[0]));
                }
            }
        });
    };

    useEffect(() => {
        loadGroupMessages();
    }, []);

    // Scroll to bottom when conversation group is selected in case of long message history
    useEffect(() => {
        bottomConvRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [conversationSelected]);

    const onSearch = (text: string) => {
        setSearchFilter(text);
    };

    const onSelect = (id: number | null) => {
        if (id === null) {
            selectConversation(`0`);
            return;
        }

        if (messengerNotifier.connection && messengerNotifier.connection.state === HubConnectionState.Connected) {
            // Read event through websocket
            messengerNotifier.connection?.send("ReadGroup", id).then(function () {
                // Get messages from API
                messengerService.ListGroupAndMessages(id).then(function (response) {
                    if (response.success && response.group) {
                        dispatch(SetGroupMessageSelected(response.group));
                        dispatch(UpdateGroupMessage(response.group));
                        let counter: number = 0;
                        groupMessages.forEach((element) => {
                            if (!element.reads?.map((user) => user.userId).includes(myUserId)) counter++;
                        });
                        dispatch(SetMessageBadgeNumber(counter));
                    }
                });
            });
        }
        selectConversation(`${id}`);
    };

    const onClickDeleteConversation = (id: number) => {
        messengerNotifier.connection?.send("DeleteGroup", id).then(function () {
            dispatch(DeleteGroupMessage(id));
        });
    };

    const onClickLeave = () => {
        if (!openModal) return;

        openModal<ModalConfirmActionElementProps>(ModalConfirmAction, {
            displayComment: false,
            title: t("modal:title.confirmLeaveDiscussion"),
            width: 504,
            onConfirm: () => {
                messengerNotifier.connection?.send("LeaveGroup", conversationSelected?.id);
                dispatch(DeleteGroupMessage(conversationSelected?.id));
                groupMessages.length > 0
                    ? onSelect(groupMessages.filter((message) => message.id !== conversationSelected?.id)[0].id)
                    : onSelect(null);
            },
        });
    };

    const onClickDeleteMessage = (message: DbMessengerMessageInfos) => {
        if (!conversationSelected?.messages?.find((x) => x.id === message.id)) return;
        messengerNotifier.connection?.send("DeleteMessage", message.id).then(function () {
            dispatch(DeleteMessage(message));
        });
    };

    const onClickSendMessage = (textMessage: string) => {
        if (!conversationSelected || textMessage.isEmpty() || textMessage.length < 2) return;
        messengerNotifier.connection
            ?.invoke("WriteMessage", conversationSelected.id, textMessage)
            .then(function (response: DbMessengerMessageInfos) {
                dispatch(NewMessage(response));
            });
    };

    const getConversationPrincipleUser = (conv: DbMessengerGroupInfos): DbUserBasicInfos => {
        if (!conv.memberInfos) return new DbUserBasicInfos();

        let filtered = conv.memberInfos!.filter((x) => x.id !== myUserId);

        if (filtered.length === 0) {
            let split = conv.ownerUserName!.split(" ");
            return {
                id: conv.ownerUserId!,
                enabled: true,
                deleted: false,
                groupId: -1,
                firstName: split[0],
                lastName: split.length > 1 ? split[1] : split[0],
                email: null,
                name: conv.ownerUserName,
                login: "",
                isNew: false,
            };
        }

        return filtered.length > 1 ? filtered[Math.floor(Math.random() * filtered.length)] : filtered[0];
    };

    return (
        <Grid container columnSpacing={2} mt={0} flex={1} sx={{ height: "100%" }}>
            <Grid
                size={{
                    md: 4,
                    xl: 3
                }}
                pr={1}
                sx={(theme) => ({
                    ...theme.helpers(theme).scrollBar,
                    overflowY: "scroll",
                    height: "100%",
                })}
                position="relative"
            >
                <Box position="sticky" top={"16px"} overflow="auto">
                    <Box
                        position="sticky"
                        top={0}
                        sx={(theme) => ({ zIndex: 1, backgroundColor: theme.palette.background.paper })}
                    >
                        <Stack direction={"row"} spacing={2} sx={{ mb: 2 }}>
                            <OutlinedInput
                                label={t("common:standard")}
                                sx={{ width: "100%" }}
                                placeholder={t("field:placeholder.searchByNameOrMember")}
                                onKeyUp={(e) => onSearch(e.currentTarget.value.toLowerCase())}
                                endAdornment={
                                    <InputAdornment position="end">
                                        <SearchIcon />
                                    </InputAdornment>
                                }
                            />
                            <Button
                                onClick={() => handleOpenModalNewConversation()}
                                variant="contained"
                                color="primary"
                                startIcon={<AddIcon />}
                            >
                                {t("common:new")}
                            </Button>
                        </Stack>
                    </Box>
                    <Stack
                        mb={3}
                        spacing={0.5}
                        position="relative"
                        sx={(theme) => ({
                            ...theme.helpers(theme).scrollBar,
                        })}
                    >
                        {groupMessages
                            .filter(
                                (x) =>
                                    x.name?.includesAllWords(searchFilter) ||
                                    x.memberInfos!.map((user) => user.name!).filter((a) => a?.includesAllWords(searchFilter)).length >
                                    0 ||
                                    x.ownerUserName?.includesAllWords(searchFilter)
                            )
                            .sort(function (a, b) {
                                if ((a.lastUpdate ? a.lastUpdate : a.creationDate)! < (b.lastUpdate ? b.lastUpdate : b.creationDate)!)
                                    return 1;
                                else return -1;
                            })
                            .map((group) => (
                                <ActivityConversation
                                    key={group.id}
                                    userId={getConversationPrincipleUser(group).id}
                                    firstName={getConversationPrincipleUser(group).firstName ?? ""}
                                    lastName={getConversationPrincipleUser(group).lastName ?? ""}
                                    variant="warning"
                                    action=""
                                    label=""
                                    user={(group.isDirectTalk ? getConversationPrincipleUser(group).name : group.name) || ""}
                                    body={
                                        group.isDirectTalk
                                            ? "Direct talk"
                                            : group.memberInfos?.map((member) => member.name).join(", ") || ""
                                    }
                                    selected={isConversationSelected(`${group.id}`)}
                                    onClick={() => onSelect(group.id)}
                                    onClickDelete={() => onClickDeleteConversation(group.id)}
                                    isOwner={group.ownerUserId === myUserId}
                                    isRead={group.reads?.map((x) => x.userId).includes(myUserId) || group.id === conversationSelected?.id}
                                    onClickEdit={() =>
                                        handleOpenModalNewConversation(group.memberInfos || undefined, group.name || undefined, group.id)
                                    }
                                />
                            ))}
                        {groupMessages.length === 0 && (
                            <EmptyListSlot
                                title={t("component:emptyListSlot.title.itIsEmptyHere")}
                                desc={t("component:emptyListSlot.desc.emptyConversation")}
                                icon={<CollectionsBookmarkOutlinedIcon />}
                                sx={{ background: "transparent", textAlign: "center" }}
                                height="100%"
                            ></EmptyListSlot>
                        )}
                    </Stack>
                </Box>
            </Grid>
            <Grid
                size={{
                    md: 8,
                    xl: 9
                }}
                flex={1}
                pr={1}
                display="flex"
                flexDirection="column"
            >
                {conversationSelected && (
                    <>
                        <Box pt={3} position="sticky" zIndex={1} bgcolor="background.default">
                            <Box pb={2}>
                                <Stack direction="row" alignItems="center" spacing={1} justifyContent="space-between">
                                    <Typography fontWeight={1000}>
                                        {conversationSelected.name || getConversationPrincipleUser(conversationSelected).name}
                                    </Typography>

                                    <Stack direction="row" spacing={1}>
                                        <IconButton onClick={toggle}>
                                            <ExpandMoreRoundedIcon fontSize="small" />
                                        </IconButton>
                                        {conversationSelected.ownerUserId !== myUserId && (
                                            <IconButton onClick={onClickLeave}>
                                                <ExitToAppIcon />
                                            </IconButton>
                                        )}
                                    </Stack>
                                </Stack>
                            </Box>
                            <Collapse in={active}>
                                <ActivityContext
                                    activityKind={ActivityKind.Action}
                                    title={conversationSelected.memberInfos?.map((member) => member.name).join(", ") || ""}
                                    subtitle={undefined}
                                    detail=""
                                    resId={0}
                                    sxLeft={{ flex: 0.5 }}
                                />
                            </Collapse>
                            <Divider />
                        </Box>
                        <Stack spacing={1} pt={2} pl={3} pr={3} flex="1 1 0" sx={(theme) => ({
                            overflowY: "auto", ...theme.helpers(theme).scrollBar,
                            height: "100%",
                            "&::-webkit-scrollbar-thumb": {
                                backgroundColor: theme.palette.background[300],
                            }
                        })}>
                            {conversationSelected.messages?.map((message) => (
                                <ActivityComment
                                    key={message.id}
                                    type="conversation"
                                    author={message.userName || ""}
                                    sx={{
                                        maxWidth: 372,
                                        alignSelf: message.userId === myUserId ? "flex-end" : "",
                                    }}
                                    isReply={message.userId === myUserId}
                                    date={message.date || undefined}
                                    id={message.id}
                                    onClickDelete={() => onClickDeleteMessage(message)}
                                    deleted={message.deleted || false}
                                >
                                    {message.text}
                                </ActivityComment>
                            ))}

                            <div ref={bottomConvRef} />
                        </Stack>

                        <Box pt={1.5} pl={3} pr={3} bgcolor="background.default" position="sticky" bottom={0}>
                            <CommentForm onSend={onClickSendMessage} />
                        </Box>
                    </>
                )}
            </Grid>
        </Grid>
    );
}

export default DashboardConversations;
